import {createFileRoute, useNavigate} from "@tanstack/react-router";
import {OttopilotColumnType} from "~/components/ottopilotDataViewer/column";
import {OttopilotDataViewer} from "~/components/ottopilotDataViewer/ottopilotDataViewer";
import {useOttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {ViewType} from "~/components/ottopilotDataViewer/view";
import {TableOnlyPage} from "~/components/tableOnlyPage";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {useWorkspace} from "~/contexts/workspaceContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import type {OttopilotAnnouncement} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotAnnouncementsWorkspaceCapability/domain/OttopilotAnnouncement";
import {AnankeLinkButton} from "@vesta/ananke/components/anankeLinkButton";
import {getAnnouncements, getAnnouncements_Length} from "~/utilities/apis/api";
import {uniqueUuid} from "@vesta/unique/unique";

export const Route = createFileRoute(
    "/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/announcements/",
)({
    loader: async () => {},
    component: RouteComponent,
});

function RouteComponent() {
    const {workspace} = useWorkspace();
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const {heimdallUser} = useWorkspaceUser();

    const announcementsDataViewerController = useOttopilotDataViewerController<OttopilotAnnouncement>({
        rowDisplayNameVariants: {
            singularSentenceCase: "OttopilotAnnouncement",
            pluralSentenceCase: "Announcements",
            singularLowerCase: "announcement",
            pluralLowerCase: "announcements",
        },
        queryKeyPrefix: [uniqueUuid`59468bb9-abe0-4b5f-8b21-d19a9b91f6cb`],
        rowsQueryFn: async ({filterConfiguration, limit, offset, sortConfiguration, searchConfiguration}) => {
            const announcementsResult = await getAnnouncements({
                workspaceId: workspace.id,
                offset: offset,
                limit: limit,
                filterConfiguration: filterConfiguration,
                sortConfiguration: sortConfiguration,
                searchConfiguration: searchConfiguration,
                intellsysBaseUrl: intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
            });
            if (announcementsResult.success !== true) {
                throw announcementsResult.error;
            }
            const announcements: Array<OttopilotAnnouncement> = announcementsResult.data;

            return announcements;
        },
        rowCountQueryFn: async ({filterConfiguration, searchConfiguration}) => {
            const announcementsLengthResult = await getAnnouncements_Length({
                workspaceId: workspace.id,
                filterConfiguration: filterConfiguration,
                searchConfiguration: searchConfiguration,
                intellsysBaseUrl: intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
            });
            if (announcementsLengthResult.success !== true) {
                throw announcementsLengthResult.error;
            }
            const announcementsLength = announcementsLengthResult.data;

            return announcementsLength;
        },
        columnValuesQueryFn: async () => {
            return [];
        },
        columns: {
            revisionId: uniqueUuid`8e90ffdf-96a6-4118-880d-e0782954e99d`,
            lineItems: [
                {
                    id: "id",
                    type: OttopilotColumnType.uuid,
                    displayName: "ID",
                    valueGetter: (announcement) => announcement.id,
                    sortingCapability: true,
                },
            ],
        },
        views: [
            {
                id: uniqueUuid`17ff9eb4-0fbf-4887-b75e-1fdb3a4aee19`,
                viewType: ViewType.table,
                visibleColumns: ["id"],
                otherArgs: {
                    rowClass: "cursor-pointer",
                    onRowClicked: (event) => {
                        navigate({
                            to: `/w/${workspace.id}/ottopilot/announcements/${event.data.id}`,
                        });
                    },
                },
            },
        ],
    });

    const navigate = useNavigate();

    return (
        <TableOnlyPage
            // breadcrumbs={["Journeys"]}
            title="Announcements"
            titleRightSection={
                <AnankeLinkButton
                    // TODO: Somehow bring back the type safety here
                    href={`/w/${workspace.id}/ottopilot/announcements/create`}
                >
                    + Add announcement
                </AnankeLinkButton>
            }
            bottomSection={<OttopilotDataViewer controller={announcementsDataViewerController} />}
        />
    );
}
